import DocumentCreationButton from 'Components/DocumentCreationButton';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import { FormContextProvider, HistoryContextProvider } from 'Containers/FormContextHandler';
import FormEditingHandler from 'Containers/FormEditingHandler';
import { sortPartialDate } from 'neuro-utils';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useAppSelector } from 'Store/index';
import { filterDocumentsByType } from 'Utility/documentHandling';
import AriaSymptomsForm from './Form';
import AriaSymptomsHistory from './HistoryRowData';

import type { DocTypesByPageName } from '../../../config/platformSettings';

const AriaSymptoms = ({ documents }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });
  const allDocs = useAppSelector(s => s.documents.sortedAndMergedDocuments) ?? [];
  const mriDocsWithAria = filterDocumentsByType(allDocs, ['mri']).filter(
    d => (d.ariaE?.finding && d.ariaE.finding !== 'no') ?? d.ariaH?.finding === 'yes',
  );

  const latestMriWithAria = mriDocsWithAria.toSorted((m1, m2) => sortPartialDate(m2.date, m1.date))[0];
  const latestAriaSymptoms = documents.toSorted((a1, a2) => sortPartialDate(a2.date, a1.date))[0];

  return (
    <FormEditingHandler name="ariaSymptoms" documents={documents}>
      {(editing, startEdit, formData, view): React.JSX.Element => (
        <DocumentWrapper>
          <DocumentHeader
            headerId={'ariaSymptoms.title'}
            editing={editing || view?.viewing}
            editButtons={
              <div>
                <DocumentCreationButton
                  name="ariaSymptoms"
                  text={'general.new'}
                  onClick={startEdit({})}
                  disabled={sortPartialDate(latestMriWithAria?.date, latestAriaSymptoms?.date) === -1}
                  disabledTooltip={<>{fm('ariaSymptoms.disabledNew')}</>}
                />
              </div>
            }
          />
          {!view?.viewing && !editing ? (
            <HistoryContextProvider context={{ documents, startEdit, view }}>
              <AriaSymptomsHistory documents={documents} />
            </HistoryContextProvider>
          ) : undefined}
          {view?.viewing || editing ? (
            <FormContextProvider context={{ documents, formData, editing, view }}>
              <AriaSymptomsForm />
            </FormContextProvider>
          ) : undefined}
        </DocumentWrapper>
      )}
    </FormEditingHandler>
  );
};

type DocTypes = DocTypesByPageName['ariaSymptoms'];

interface IOwnProps {
  documents: IDocumentWithTypeV2<DocTypes>[];
}

export default AriaSymptoms;
